import type { Ref } from "vue";
import type { RouteLocationRaw } from "vue-router";
import router from "@/router";

export interface CallForAction {
    action: string;
    text: string;
}

export type ApiMessageLevel = "success" | "info" | "warning" | "error";

export interface ApiMessage {
    level: string;
    text: string;
    style?: string;
    title?: string;
    call_for_action?: CallForAction;
    variable_id?: string | number;
    router_links?: Record<string, RouteLocationRaw>;
}

export interface ApiResponse {
    messages?: ApiMessage[];
}

export interface RestResponse {
    status: number;
    json?: ApiResponse;
    textData?: string;
}

export type Methods = "GET" | "POST" | "PUT" | "DELETE";

export type URLParams = Record<string, string | number | boolean | number[] | string[]> | undefined | null;

/**
 * Web-service client, in charge of creating URL, error and status handling and request construction and response parsing.
 */
export class WSClient {
    static readonly rootApi = import.meta.env.VITE_WS_URL || "";
    loading?: Ref<boolean>;
    messages?: Ref<ApiMessage[]>;
    status_code?: number = -1;
    resetMessages?: boolean = false;
    no_redirect?: boolean;
    no_403_redirect?: boolean;
    abort_controler?: AbortController;

    /**
     * Create web-service client
     *
     * @param {Ref<boolean> | undefined} loading - to inform if the query is running.
     * @param {Ref<ApiMessage[]> | undefined} messages - to return possible feedback messages.
     * @param {boolean | undefined} no_redirect - Don't redirect to login or paywall if 401 or 402 is returned.
     * @param {boolean} [resetMessages=true] - Blank feed-back message list before request.
     * @param {boolean} [no_403_redirect=true] - Don't redirect to not allowed page if 403 is returned.
     */
    constructor(
        loading?: Ref<boolean>,
        messages?: Ref<ApiMessage[]>,
        no_redirect?: boolean,
        resetMessages = true,
        no_403_redirect = true
    ) {
        this.loading = loading;
        this.messages = messages;
        this.no_redirect = no_redirect;
        this.no_403_redirect = no_403_redirect ?? true;
        this.resetMessages = resetMessages;
    }

    /**
     * Low level query, handle loading and redirection if not auth.
     *
     * @param method
     * @param url
     * @param data
     * @param headers
     * @returns
     */
    async query(
        method: Methods,
        url: string,
        data: BodyInit | FormData | null | undefined,
        headers: HeadersInit | undefined
    ): Promise<Response> {
        this.onLoading(true);

        this.abort_controler = new AbortController();
        const signal = this.abort_controler.signal;

        const httpResponse = await fetch(url, {
            mode: "cors",
            credentials: "include",
            method: method,
            headers: headers,
            body: data,
            signal,
        });
        this.onLoading(false);
        this.status_code = httpResponse.status;
        this.abort_controler = undefined;
        if (router) {
            const route = router.currentRoute.value;
            const errCode = httpResponse.status;
            if (route.name != "login") {
                if ((errCode == 401 || errCode == 409) && !this.no_redirect) {
                    router.push({
                        name: "login",
                        query: { error: errCode, redir_on_success: route.path },
                    });
                } else if (errCode == 402 && !this.no_redirect) {
                    router.push({
                        name: "EcrfPaywall",
                        query: { error: 402, redir_on_success: route.path },
                    });
                } else if (errCode == 403 && !this.no_403_redirect) {
                    router.push({
                        name: "NotAllowedInSerie",
                    });
                }
            }
        }

        return httpResponse;
    }

    /**
     * Query adapted to generic JSON WS.
     * Handle messages and non JSON responses.
     *
     * @param method
     * @param url
     * @param data
     * @returns
     */
    async queryAllTypes(method: Methods, url: string, data: unknown, queryType: string): Promise<any> {
        let headers = new Headers({ "Content-Type": "application/json" });
        let body_param;
        if (queryType === "form") {
            headers = new Headers({ Accept: "application/json" });
            body_param = data ? (data as BodyInit) : null;
        } else {
            body_param = data ? JSON.stringify(data) : null;
        }
        const httpResponse = await this.query(method, url, body_param, headers);
        const contentType = httpResponse.headers.get("Content-type");
        if (contentType && contentType.startsWith("application/json")) {
            try {
                const json = await httpResponse.json();

                if (!json) {
                    return this.handleText(httpResponse.ok, await httpResponse.text());
                }

                this.setMessages(json?.messages);
                if (httpResponse.status > 199 && httpResponse.status < 300) {
                    return Promise.resolve(json);
                }
                return Promise.reject(json);
            } catch (err) {
                return this.handleError(err);
            }
        }

        return this.handleText(httpResponse.ok, await httpResponse.text());
    }

    handleText(isOk: boolean, text: string): Promise<any> {
        if (isOk) {
            return Promise.resolve({ text: text });
        }
        this.setMessages([{ level: "error", text: "Error " + text }]);
        return Promise.reject();
    }

    handleError(error: unknown): Promise<ApiResponse> {
        this.onLoading(false);

        if (Array.isArray(error)) {
            this.setMessages(error);
        } else {
            const message = error instanceof Error ? error.message : String(error);
            this.setMessages([{ level: "error", text: "Error " + message }]);
        }

        return Promise.reject(error);
    }

    setMessages(input: ApiMessage[] | undefined): void {
        if (this.messages) {
            if (this.resetMessages) {
                this.messages.value = [];
            }
            const m_arr = input;
            if (m_arr) {
                this.messages.value.push(...m_arr);
            }
        }
    }

    onLoading(isLoading: boolean): void {
        if (this.loading) {
            this.loading.value = isLoading;
        }
    }

    getWsUrl(
        service: string,
        urlParams: URLParams
    ): string {
        return WSClient.rootApi + "/ws.php?service=" + service + this.toQueryString(urlParams);
    }

    toQueryString(arr: URLParams): string {
        if (!arr) return "";
        const params = [] as string[];
        Object.keys(arr)
            .filter((key) => typeof arr[key] != "undefined")
            ?.forEach((key) => {
                const k = `${key}=`,
                    v = arr[key];
                if (v === null) return;
                if (Array.isArray(v)) {
                    v?.forEach((v2) => {
                        params.push(k + encodeURIComponent(v2.toString()));
                    });
                } else {
                    params.push(k + encodeURIComponent(v.toString()));
                }
            });
        return "&" + params.join("&");
    }

    queryWs<T>(
        method: Methods,
        service: string,
        urlParams?: URLParams,
        body?: unknown,
        queryType = "json"
    ): Promise<T> {
        return this.queryAllTypes(method, this.getWsUrl(service, urlParams), body, queryType);
    }

    abort(reason: string): void {
        this.abort_controler?.abort(reason);
    }
}
